import { useIntl } from 'react-intl';
import { LoadingSkeleton, Selector, useQueryFetch } from 'sonata-react-components';

import { getAdvertisers } from '@/services/advertiser.service';
import { useGlobalStore } from '@/store/global';
import { getSessionByKeys } from '@/store/global/session/selectors.ts';

import type { MediaAdvertiserUserInterface } from 'sonata-static-storage/lib/schemas/mediaInfo/mediaAdvertiserUser.schema';

type Props = {
  id: string;
  onChange: (advertiser: MediaAdvertiserUserInterface) => void;
};

export function AdvertiserSelector({ id, onChange }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));
  const token: Record<string, any> = {};

  const { data: paginatedAdvertisers, isLoading: areAdvertisersLoading } = useQueryFetch(
    ['advertisersSelector'],
    async () =>
      getAdvertisers(
        userId,
        {
          sortBy: 'modified',
          sortOrder: 'desc',
          unlimited: true
        },
        token
      )
  );

  const advertiserItems: MediaAdvertiserUserInterface[] | undefined = paginatedAdvertisers?.items;

  const handleAdvertiserChange = (value: { id: string; label: string }) => {
    const advertiser = advertiserItems?.find(c => c._id === value.id) as MediaAdvertiserUserInterface;
    onChange(advertiser);
  };

  const advertiserOptions =
    paginatedAdvertisers?.items.map(c => ({
      id: c._id as string,
      label: c.name
    })) || [];

  if (areAdvertisersLoading || !advertiserOptions) {
    return <LoadingSkeleton className="advertiserSelector" height={40} width="100%" />;
  }

  return (
    <Selector
      className="advertiserSelector"
      id={id}
      label={formatMessage({
        defaultMessage: 'Advertiser:',
        id: 'campaignPlans.modal.advertiserSelectorLabel'
      })}
      onChange={handleAdvertiserChange}
      options={advertiserOptions}
      placeholder={formatMessage({
        defaultMessage: 'Select Advertiser',
        id: 'advertisers.advertiserSelector.placeholder'
      })}
      type="simple"
    />
  );
}
